import {
  useMemo,
  useCallback,
} from 'react';
import {
  selection,
} from 'redux-resource-plugins';
import {
  Droppable,
  DragDropContext,
} from '@hello-pangea/dnd';
import {
  getStatus,
} from 'redux-resource';
import i18n from 'i18next';
import {
  toast,
} from 'react-toastify';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreUtils,
  coreActions,
  coreSelectors,
  WorkoutType,
  WorkoutExercise as WorkoutExerciseType,
} from '@gymcloud/core';

import {
  ID,
  arrayMove,
  updateItemPositonOnList,
} from 'web-utils';

import {
  getUiState,
  getFeatures,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';

import {
  Spinner,
  WorkoutExercise,
  DieRollSetupModal,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import * as S from './styled';

type Props = {
  type: string;
  readOnly?: boolean;
  workoutId: number;
  workoutType: WorkoutType;
};

const WorkoutExercisesList = ({
  type,
  readOnly = false,
  workoutId,
  workoutType,
}: Props) => {
  const resourceType = 'workoutExercises';
  const mapState = useCallback(state => ({
    updateStatus: getStatus(
      state,
      [
        'workoutExercises.requests.addExercise.status',
        'workoutExercises.requests.updateOrder.status',
        'workoutExercises.requests.setOrderName.status',
        'workoutExercises.requests.addAlternative.status',
        'exerciseProperties.requests.deleteEntity.status',
        'exerciseProperties.requests.assignProperties.status',
        'exerciseProperties.requests.saveExerciseProperty.status',
      ],
      false,
    ),
    features: getFeatures(state),
    exercises: coreSelectors.getSortedListWorkoutExercises(
      workoutId,
      true,
    )(state) as WorkoutExerciseType[],
    visibleSelect: coreSelectors.getWorkoutProperties(
      workoutId,
    )(state).length > 0,
    defaultProperty: coreSelectors.getDefaultProperty(state),
    ...getUiState([
      'dieRollSetupModal',
    ])(state),
  }), [workoutId]);

  const {
    features,
    exercises,
    updateStatus,
    visibleSelect,
    defaultProperty,
    dieRollSetupModal,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const numbers = useMemo(() => exercises.map(item => item.number), [exercises]);

  const handleSelect = (exerciseId: number, selected: boolean) => {
    dispatch(selected
      ? selection.selectResources(resourceType, [exerciseId])
      : selection.deselectResources(resourceType, [exerciseId]));
  };

  const handleRequestUpdate = (exerciseId: number, payload: any) => {
    dispatch(
      coreActions.updateResources({
        resourceType,
        resources: [{
          id: exerciseId,
          ...payload,
        }],
      }),
    );
    dispatch(
      coreActions.requestUpdateEntity({
        id: exerciseId,
        payload,
        resourceType,
      }),
    );
  };

  const handleDelete = (exercise: WorkoutExerciseType) => {
    const onSuccess = () => {
      const resources = coreUtils.updateOrderNameExercises(
        exercises.filter(e => e.id !== exercise.id),
      );
      dispatch(
        coreActions.updateResources({
          resourceType,
          resources,
        }),
      );

      dispatch(
        coreActions.requestUpdateItems({
          resources,
          resourceType,
        }),
      );
    };

    const onConfirmAction = coreActions.requestDeleteItems({
      resourceType,
      resources: [exercise, ...exercise.alternatives].map(e => e.id),
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Exercise',
          bodyText: `Are you sure you want to delete <b>${exercise.name}</b>?`,
        },
        onSuccess,
        onConfirmAction,
      }),
    );
  };

  const handleReplace = ({ id }: WorkoutExerciseType) => {
    const onSelect = (newExerciseId: number) => {
      dispatch(
        coreActions.requestReplaceExerciseInWorkout({
          workoutId,
          exerciseId: id,
          newExerciseId,
          workoutType,
        }),
      );
    };

    dispatch(
      uiActions.openExercisePicker({
        onSelect,
      }),
    );
  };

  const handleSetOrderName = (exerciseId: number, newNumber: number) => {
    dispatch(
      coreActions.requestSetExerciseOrderName({
        workoutId,
        exerciseId,
        newNumber,
      }),
    );
  };

  const handleAddProperty = (payload: any) => {
    dispatch(
      coreActions.addResources({
        resourceType: 'exerciseProperties',
        resources: [{
          id: ID(),
          isNew: true,
          ...payload,
          ...defaultProperty,
        }],
        list: `workout_${workoutId}`,
      }),
    );
  };

  const handleSaveProperty = (propertyId: number, payload: any, isNew: boolean) => {
    dispatch(
      coreActions.requestSaveExerciseProperty({
        propertyId,
        payload,
        isNew,
        list: `workout_${workoutId}`,
      }),
    );
  };

  const handleDeleteProperty = (propertyId: number, isNew: boolean) => {
    dispatch(isNew
      ? coreActions.deleteResources({
        resourceType: 'exerciseProperties',
        resources: [{
          id: propertyId,
        }],
      })
      : coreActions.requestDeleteEntity({
        id: propertyId,
        resourceType: 'exerciseProperties',
      }));
  };

  const handleDragEnd = (result: any) => {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    // same position
    if (source.index === destination.index) {
      return;
    }

    if (result.type === 'property') {
      const exerciseId = +destination.droppableId;
      const list = Array.from(
        exercises.find(e => e.id === exerciseId)?.properties || [],
      );
      const resources = updateItemPositonOnList(
        arrayMove(
          list,
          source.index,
          destination.index,
        ),
      );

      dispatch(
        coreActions.updateResources({
          resourceType: 'exerciseProperties',
          resources,
        }),
      );

      dispatch(
        coreActions.requestUpdateItems({
          resources,
          resourceType: 'exerciseProperties',
        }),
      );

      dispatch({
        type: 'GC_ANALYTICS',
        eventKey: 'changed_property_position',
      });
    } else {
      const resources = coreUtils.updateOrderNameExercises(
        arrayMove(
          exercises,
          source.index,
          destination.index,
        ),
      );

      dispatch(
        coreActions.updateResources({
          resourceType,
          resources,
        }),
      );

      dispatch(
        coreActions.requestUpdateItems({
          resources,
          resourceType,
        }),
      );

      dispatch({
        type: 'GC_ANALYTICS',
        eventKey: 'changed_exercise_position',
      });
    }
  };

  const handleAddAlternative = (exerciseId: number) => {
    const onSelect = (alternativeId: number) => {
      dispatch(
        coreActions.requestAddAlternativeExercise({
          workoutId,
          exerciseId,
          alternativeId,
          workoutType,
        }),
      );
    };

    dispatch(
      uiActions.openExercisePicker({
        onSelect,
      }),
    );
  };

  const handleSetAlternative = (exerciseId: number, alternativeId: number) => {
    dispatch(
      coreActions.requestSetAlternativeExercise({
        exerciseId,
        alternativeId,
      }),
    );
  };

  const handleRemoveAlternative = (alternative: WorkoutExerciseType) => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id: alternative.id,
      resourceType,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Alternative?',
          bodyText: `Are you sure you want to delete alternative <b>${alternative.name}</b>?`,
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };

  const handleDieRoll = (exercise: WorkoutExerciseType) => {
    dispatch(
      uiActions.setUiState({
        dieRollSetupModal: {
          isOpen: true,
          name: `${exercise.workout_name}/${exercise.name}`,
          exercise,
        },
      }),
    );
  };

  const handleToggleDieRoll = (exercise: WorkoutExerciseType) => {
    if (!exercise.properties.length) {
      toast.error(i18n.t('workoutExercises.property.warn.empty'));
      return;
    }

    if (exercise.hasDieRoll) {
      const properties = exercise.properties
        .filter(
          p => p.dieroll_options.length,
        )
        .map(
          p => ({
            ...p,
            dieroll_options_attributes: p.dieroll_options.map(
              option => ({
                ...option,
                _destroy: true,
              }),
            ),
          }),
        );

      if (properties.length) {
        dispatch(
          coreActions.requestSaveDieRollOptions({
            properties,
          }),
        );
      }
    }

    const hasDieRoll = !exercise.hasDieRoll;
    handleRequestUpdate(exercise.id, { hasDieRoll, has_die_roll: hasDieRoll });
  };

  if (exercises.length === 0) {
    return (
      <S.Empty>
        {i18n.t('there.no.exercises', { type: `$t(${type})` })}
      </S.Empty>
    );
  }

  const allExercises = exercises.map((exercise, index) => (
    <WorkoutExercise
      key={exercise.id}
      index={index}
      readOnly={readOnly}
      exercise={exercise}
      number={numbers[index]}
      numbers={numbers.filter(n => exercise.number !== n)}
      prevNumber={numbers[index - 1]}
      nextNumber={numbers[index + 1]}
      properties={exercise.properties}
      canDieRoll={features.die_roll}
      visibleSelect={visibleSelect}
      onSelect={handleSelect}
      onRequestUpdate={handleRequestUpdate}
      onRequestDelete={handleDelete}
      onRequestReplace={handleReplace}
      onToggleDieRoll={handleToggleDieRoll}
      onRequestDieRoll={handleDieRoll}
      onRequestSetOrderName={handleSetOrderName}
      onRequestAddProperty={handleAddProperty}
      onRequestSaveProperty={handleSaveProperty}
      onRequestDeleteProperty={handleDeleteProperty}
      onRequestAddAlternative={handleAddAlternative}
      onRequestSetAlternative={handleSetAlternative}
      onRequestRemoveAlternative={handleRemoveAlternative}
    />
  ));

  return (
    <Flex fillSpace>
      {readOnly
        ? (
          <Flex
            width="100%"
            column
          >
            {allExercises}
          </Flex>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
              type="exercise"
              droppableId="workout"
            >
              {droppableProvided => (
                <Flex
                  ref={droppableProvided.innerRef}
                  width="100%"
                  column
                >
                  {allExercises}
                  {droppableProvided.placeholder}
                </Flex>
              )}
            </Droppable>
          </DragDropContext>
        )}

      {updateStatus.pending && (
        <Spinner over />
      )}

      <DieRollSetupModal
        name={dieRollSetupModal.name}
        isOpen={dieRollSetupModal.isOpen}
        exercise={dieRollSetupModal.exercise}
        onRequestSubmit={(data, actions) => {
          const close = () => {
            dispatch(
              uiActions.setUiState({
                dieRollSetupModal: {
                  isOpen: false,
                },
              }),
            );
          };
          dispatch(
            coreActions.requestSaveDieRollOptions({
              properties: data,
              callbacks: {
                ...actions,
                onSuccess: close,
                onError: close,
              },
            }),
          );
        }}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              dieRollSetupModal: {
                isOpen: false,
              },
            }),
          );
        }}
      />
    </Flex>
  );
};

export default WorkoutExercisesList;
